import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, Store, PlugZap, CheckCircle2, Loader2, ExternalLink, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SellingHubPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Shopify
  const [shopDomain, setShopDomain] = useState('');
  const [shopToken, setShopToken] = useState('');
  const [connectingShopify, setConnectingShopify] = useState(false);

  // Woo
  const [wooUrl, setWooUrl] = useState('');
  const [wooKey, setWooKey] = useState('');
  const [wooSecret, setWooSecret] = useState('');
  const [connectingWoo, setConnectingWoo] = useState(false);

  const [shopifyOrders, setShopifyOrders] = useState(null);
  const [wooOrders, setWooOrders] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/ecommerce/summary`, { headers: auth });
      const d = await r.json();
      setSummary(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const connectShopify = async () => {
    setConnectingShopify(true);
    try {
      const r = await fetch(`${API}/ecommerce/shopify/connect`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({ shop_domain: shopDomain, access_token: shopToken }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Shopify connect failed');
      toast.success(`Connected to ${d.shop_name || d.shop_domain}`);
      setShopDomain(''); setShopToken(''); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setConnectingShopify(false); }
  };

  const connectWoo = async () => {
    setConnectingWoo(true);
    try {
      const r = await fetch(`${API}/ecommerce/woocommerce/connect`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({ site_url: wooUrl, consumer_key: wooKey, consumer_secret: wooSecret }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'WooCommerce connect failed');
      toast.success(`Connected to ${d.site_title || d.site_url}`);
      setWooUrl(''); setWooKey(''); setWooSecret(''); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setConnectingWoo(false); }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}?`)) return;
    await fetch(`${API}/ecommerce/${provider}/disconnect`, { method: 'POST', headers: auth });
    toast.success('Disconnected');
    refresh();
  };

  const loadOrders = async (provider) => {
    const r = await fetch(`${API}/ecommerce/${provider}/orders`, { headers: auth });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Failed to load orders'); return; }
    if (provider === 'shopify') setShopifyOrders(d.orders || []);
    else setWooOrders(d.orders || []);
    toast.success(`Loaded ${((provider === 'shopify' ? d.orders : d.orders) || []).length} orders`);
  };

  const shopifyConnected = summary?.shopify_connected;
  const wooConnected = summary?.woocommerce_connected;
  const shopifyIntegration = (summary?.integrations || []).find(i => i.provider === 'shopify');
  const wooIntegration = (summary?.integrations || []).find(i => i.provider === 'woocommerce');

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fade-in" data-testid="selling-hub-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <ShoppingCart className="w-7 h-7 text-indigo-500" /> Selling Hub
        </h1>
        <p className="text-slate-500 mt-1">
          Connect your Shopify and WooCommerce stores. Orders pulled here inherit every SelleSend guardrail —
          Selle10 insurance, pre-label fraud gate, and rate-shop across all carriers.
        </p>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          {/* Shopify */}
          <Card className="border-emerald-100 dark:border-emerald-900/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-500" /> Shopify
                {shopifyConnected && <Badge variant="secondary" className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>}
              </CardTitle>
              <CardDescription>Admin API access token (starts with <code>shpat_</code>).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {shopifyConnected ? (
                <div className="space-y-2">
                  <div className="text-sm">
                    <div><b>Shop:</b> {shopifyIntegration?.shop_name || shopifyIntegration?.shop_domain}</div>
                    <div className="text-slate-500">{shopifyIntegration?.shop_domain} · {shopifyIntegration?.currency}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => loadOrders('shopify')} data-testid="shopify-load-orders">
                      <Package className="w-4 h-4 mr-1" /> Load recent orders
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => disconnect('shopify')}>Disconnect</Button>
                  </div>
                  {shopifyOrders && (
                    <div className="mt-3 border rounded-md divide-y max-h-64 overflow-auto text-sm">
                      {shopifyOrders.slice(0, 15).map(o => (
                        <div key={o.id} className="p-2 flex justify-between">
                          <span>#{o.order_number || o.id} — {o.email}</span>
                          <span className="font-semibold">${o.total_price}</span>
                        </div>
                      ))}
                      {shopifyOrders.length === 0 && <div className="p-3 text-slate-500">No orders yet.</div>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Store domain</Label>
                    <Input placeholder="my-shop" value={shopDomain} onChange={e => setShopDomain(e.target.value)} data-testid="shopify-domain" />
                  </div>
                  <div>
                    <Label>Access token</Label>
                    <Input type="password" placeholder="shpat_..." value={shopToken} onChange={e => setShopToken(e.target.value)} data-testid="shopify-token" />
                  </div>
                  <div className="sm:col-span-2">
                    <Button onClick={connectShopify} disabled={connectingShopify || !shopDomain || !shopToken} data-testid="shopify-connect-btn">
                      {connectingShopify ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlugZap className="w-4 h-4 mr-2" />}
                      Connect Shopify
                    </Button>
                    <a href="https://help.shopify.com/en/manual/apps/app-types/custom-apps" target="_blank" rel="noreferrer" className="ml-3 text-xs text-indigo-500 hover:underline inline-flex items-center gap-1">
                      Where to get a token <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* WooCommerce */}
          <Card className="border-purple-100 dark:border-purple-900/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-purple-500" /> WooCommerce
                {wooConnected && <Badge variant="secondary" className="bg-purple-100 text-purple-800"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>}
              </CardTitle>
              <CardDescription>REST API — consumer key (<code>ck_</code>) + consumer secret (<code>cs_</code>).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {wooConnected ? (
                <div className="space-y-2">
                  <div className="text-sm">
                    <div><b>Site:</b> {wooIntegration?.site_title || wooIntegration?.site_url}</div>
                    <div className="text-slate-500">{wooIntegration?.site_url} · WC {wooIntegration?.wc_version}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => loadOrders('woocommerce')} data-testid="woo-load-orders">
                      <Package className="w-4 h-4 mr-1" /> Load recent orders
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => disconnect('woocommerce')}>Disconnect</Button>
                  </div>
                  {wooOrders && (
                    <div className="mt-3 border rounded-md divide-y max-h-64 overflow-auto text-sm">
                      {wooOrders.slice(0, 15).map(o => (
                        <div key={o.id} className="p-2 flex justify-between">
                          <span>#{o.number || o.id} — {o.billing?.email}</span>
                          <span className="font-semibold">${o.total}</span>
                        </div>
                      ))}
                      {wooOrders.length === 0 && <div className="p-3 text-slate-500">No orders yet.</div>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3">
                    <Label>Site URL</Label>
                    <Input placeholder="https://mystore.com" value={wooUrl} onChange={e => setWooUrl(e.target.value)} data-testid="woo-url" />
                  </div>
                  <div>
                    <Label>Consumer key</Label>
                    <Input placeholder="ck_..." value={wooKey} onChange={e => setWooKey(e.target.value)} data-testid="woo-key" />
                  </div>
                  <div>
                    <Label>Consumer secret</Label>
                    <Input type="password" placeholder="cs_..." value={wooSecret} onChange={e => setWooSecret(e.target.value)} data-testid="woo-secret" />
                  </div>
                  <div className="sm:col-span-3">
                    <Button onClick={connectWoo} disabled={connectingWoo || !wooUrl || !wooKey || !wooSecret} data-testid="woo-connect-btn">
                      {connectingWoo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlugZap className="w-4 h-4 mr-2" />}
                      Connect WooCommerce
                    </Button>
                    <a href="https://woocommerce.com/document/woocommerce-rest-api/" target="_blank" rel="noreferrer" className="ml-3 text-xs text-indigo-500 hover:underline inline-flex items-center gap-1">
                      Where to get keys <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-50 dark:bg-slate-900/50">
            <CardHeader>
              <CardTitle className="text-base">What happens after you connect</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <div>· Every synced order is rate-shopped across all 5 carrier gateways.</div>
              <div>· Every label passes the pre-label fraud gate <b>and</b> gets Selle10/Parcel Risk insurance before printing.</div>
              <div>· SelleGard Freight rates apply automatically.</div>
              <div>· Weekly rebill statements roll up under <code>/dashboard/sellegard → Billing</code>.</div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
